import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
import RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsReferralProgramQuery from "@apps/rewards/modules/ReferralProgram/queries/RewardsReferralProgramQuery";
import RewardsReferralProgramService from "@apps/rewards/modules/ReferralProgram/services/RewardsReferralProgramService";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { MoneyModule } from "@modules/money/MoneyModule";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsReferralProgramPageWidgetComponent from "./RewardsReferralProgramPageWidget.svelte";
import type { RewardsReferralProgramPageWidgetConfig } from "./RewardsReferralProgramPageWidgetConfigSchema";

@injectable()
class RewardsReferralProgramPageWidget implements WidgetInterface {
    constructor(
        private readonly config: RewardsReferralProgramPageWidgetConfig,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsReferralProgramPageWidget is initialized");
    }

    async init(): Promise<this> {
        const widgetNamespace =
            TRANSLATION_NAMESPACES.referralProgramPageWidget;
        const pointsNamespace = TRANSLATION_NAMESPACES.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            i18nModule,
            referralProgramService,
            storefrontValuesService,
            referralProgramQuery,
            programAccountQuery,
            amountFormatter,
            currencyFormatter,
            growaveConfiguration,
            commonModule,
            moneyModule,
            authenticationModule,
            customizationManager,
        } = diResolverService.resolveAll({
            i18nModule: I18nModule,
            referralProgramService: RewardsReferralProgramService,
            storefrontValuesService: RewardsStorefrontValuesService,
            referralProgramQuery: RewardsReferralProgramQuery,
            programAccountQuery: RewardsProgramAccountQuery,
            amountFormatter: RewardsAmountFormatter,
            currencyFormatter: RewardsCurrencyFormatter,
            growaveConfiguration: growaveConfigurationToken,
            commonModule: CommonModule,
            moneyModule: MoneyModule,
            authenticationModule: AuthenticationModule,
            customizationManager: CustomizationManager,
        });

        const { i18n, i18nApiLoaderFactory, i18nCriticalLoaderFactory } =
            i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nApiLoaderFactory.getLoader(widgetNamespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );

        const { locationController, urlsBuilder, designModeProvider } =
            commonModule.exports;
        const { htmlSanitizer: sanitizer } = moneyModule.exports;

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName:
                REWARDS_WIDGET_NAMES.REWARDS_REFERRAL_PROGRAM_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsReferralProgramPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                referralProgramService,
                storefrontValuesService,
                referralProgramQuery,
                programAccountQuery,
                amountFormatter,
                currencyFormatter,
                growaveConfiguration,
                locationController,
                urlsBuilder,
                authenticationInfoProvider:
                    authenticationModule.exports.authenticationInfoProvider,
                sanitizer,
                i18n,
                namespace: widgetNamespace,
                pointsNamespace,
                customerImageUrl: this.config.customerImageUrl,
                designModeProvider,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default RewardsReferralProgramPageWidget;
